import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Activity, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';

/**
 * Phase 75 — System Health badge. Polls /system/health-status every 60s and shows a
 * single coloured pill in the top bar. Click opens a popover with per-component state.
 */
export default function SystemHealthBadge() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await api.get('/system/health-status');
        if (!cancelled) setHealth(res.data);
      } catch {
        if (!cancelled) setHealth(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    tick();
    const id = setInterval(tick, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-muted text-[11px]">
        <Loader2 className="h-3 w-3 animate-spin" />
        <span className="hidden sm:inline">Health…</span>
      </div>
    );
  }
  if (!health) return null;

  const failing = (health.components || []).filter(c => c.status !== 'ok');
  const isHealthy = failing.length === 0;
  const pillStyle = isHealthy
    ? 'bg-emerald-100 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800'
    : 'bg-amber-100 dark:bg-amber-950/30 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-800';

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium transition-colors duration-150 hover:opacity-80 ${pillStyle}`}
          data-testid="system-health-badge"
        >
          {isHealthy ? <CheckCircle2 className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
          <span className="hidden sm:inline">{isHealthy ? 'All Systems Operational' : `${failing.length} not configured`}</span>
          <span className="sm:hidden">{isHealthy ? '✓' : `${failing.length}!`}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-0" data-testid="system-health-popover">
        <div className="p-3 border-b flex items-center gap-2">
          <Activity className="h-4 w-4 text-primary" />
          <div>
            <p className="text-sm font-semibold">System Health</p>
            <p className="text-[10px] text-muted-foreground">Updated {new Date(health.timestamp).toLocaleTimeString()}</p>
          </div>
        </div>
        <div className="p-2 space-y-1">
          {(health.components || []).map(c => (
            <div key={c.id} className="flex items-center justify-between p-2 rounded hover:bg-muted" data-testid={`health-component-${c.id}`}>
              <span className="text-xs">{c.label}</span>
              {c.status === 'ok' ? (
                <span className="flex items-center gap-1 text-[11px] text-emerald-700 font-medium"><CheckCircle2 className="h-3 w-3" /> OK</span>
              ) : c.status === 'down' ? (
                <span className="flex items-center gap-1 text-[11px] text-red-700 font-medium"><AlertCircle className="h-3 w-3" /> Down</span>
              ) : (
                <span className="flex items-center gap-1 text-[11px] text-amber-700 font-medium"><AlertCircle className="h-3 w-3" /> Not configured</span>
              )}
            </div>
          ))}
        </div>
        {!isHealthy && (
          <div className="p-3 border-t bg-muted/40">
            <a href="/settings" className="text-[11px] text-primary hover:underline font-medium">Configure missing integrations →</a>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
